"use client";

import { useCallback, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Textarea } from "@/app/components/ui/textarea";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Eye, AlertTriangle, Loader2 } from "lucide-react";
import FileUpload from "@/app/components/FileUpload";

export default function ObjectDetectionPage() {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [providers, setProviders] = useState("amazon,google");
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const detectObjects = trpc.objectDetection.detect.useMutation();

  const handleFileChange = useCallback((file: File | null) => {
    if (file) {
      setUploadedFile(file);
      setError(null);
      setResults(null);
      console.log("File selected:", file.name, file.type, file.size);
    } else {
      setUploadedFile(null);
      setError(null);
      setResults(null);
    }
  }, []);

  const processImage = useCallback(async () => {
    if (!uploadedFile) {
      setError("Please select an image file first");
      return;
    }

    setError(null);
    setResults(null);
    setIsProcessing(true);

    try {
      const base64 = await fileToBase64(uploadedFile);
      
      console.log("Sending object detection request:", {
        imageSize: uploadedFile.size,
        providers,
        base64Length: base64.length,
        filename: uploadedFile.name,
        contentType: uploadedFile.type
      });
      
      const res = await detectObjects.mutateAsync({
        imageBase64: base64,
        filename: uploadedFile.name,
        contentType: uploadedFile.type,
        providers,
      });
      
      console.log("Object detection response:", res);
      setResults(res);
    } catch (e) {
      console.error("Object detection error:", e);
      setError(e instanceof Error ? e.message : "Failed to detect objects in image");
    } finally {
      setIsProcessing(false);
    }
  }, [uploadedFile, providers, detectObjects]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const formatResults = (results: any) => {
    if (!results || !results.raw) return "No results available";
    
    try {
      const raw = results.raw;
      let formatted = "Object Detection Results:\n\n";
      
      if (raw.results) {
        for (const [provider, data] of Object.entries(raw.results)) {
          formatted += `=== ${provider.toUpperCase()} ===\n`;
          
          if (data && typeof data === 'object') {
            const providerData = data as any;
            
            // Handle different response structures from various providers
            let objects: any[] = [];
            
            // Check for different possible object arrays
            if (providerData.items && Array.isArray(providerData.items)) {
              objects = providerData.items;
            } else if (providerData.objects && Array.isArray(providerData.objects)) {
              objects = providerData.objects;
            } else if (providerData.detections && Array.isArray(providerData.detections)) {
              objects = providerData.detections;
            } else if (providerData.responses && Array.isArray(providerData.responses)) {
              objects = providerData.responses;
            } else if (Array.isArray(providerData)) {
              objects = providerData;
            }
            
            if (objects.length > 0) {
              objects.forEach((item: any, index: number) => {
                // Handle different label/name fields
                const label = item.label || item.name || item.class || item.object || 'Unknown Object';
                formatted += `${index + 1}. ${label}\n`;
                
                // Handle different confidence fields
                const confidence = item.confidence || item.score || item.probability;
                if (confidence !== undefined) {
                  const confidencePercent = confidence > 1 ? confidence : (confidence * 100);
                  formatted += `   Confidence: ${confidencePercent.toFixed(1)}%\n`;
                }
                
                // Handle different bounding box structures
                const bbox = item.bounding_box || item.bbox || item.box || item.location;
                if (bbox) {
                  if (bbox.x !== undefined && bbox.y !== undefined) {
                    formatted += `   Location: x=${bbox.x}, y=${bbox.y}`;
                    if (bbox.width !== undefined && bbox.height !== undefined) {
                      formatted += `, width=${bbox.width}, height=${bbox.height}`;
                    }
                    formatted += `\n`;
                  } else if (bbox.left !== undefined && bbox.top !== undefined) {
                    formatted += `   Location: left=${bbox.left}, top=${bbox.top}`;
                    if (bbox.right !== undefined && bbox.bottom !== undefined) {
                      formatted += `, right=${bbox.right}, bottom=${bbox.bottom}`;
                    }
                    formatted += `\n`;
                  }
                }
                
                // Add any additional metadata
                if (item.category) {
                  formatted += `   Category: ${item.category}\n`;
                }
                if (item.type) {
                  formatted += `   Type: ${item.type}\n`;
                }
                
                formatted += "\n";
              });
            } else {
              formatted += "No objects detected\n\n";
            }
          } else {
            formatted += "No objects detected\n\n";
          }
        }
      } else {
        formatted += "No detection results found";
      }
      
      return formatted;
    } catch (error) {
      return `Error formatting results: ${error}`;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">Object Detection</h1>
          <p className="text-muted-foreground">Detect and identify objects in images using AI</p>
        </div>

                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
           <div className="space-y-4">
             <FileUpload
               id="object-detection-upload"
               label="Upload Image"
               accept="image/*,.jpg,.jpeg,.png,.gif,.webp,.bmp"
               maxSize={10}
               onFileChangeAction={handleFileChange}
               disabled={isProcessing}
               showPreview={true}
               file={uploadedFile}
               placeholder="Choose an image or drag it here"
               error={error || undefined}
             />
           </div>

          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Detection Settings
              </CardTitle>
              <CardDescription>Configure AI providers for object detection</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">AI Providers</label>
                <Select value={providers} onValueChange={setProviders}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select providers" />
                  </SelectTrigger>
                                   <SelectContent>
                   <SelectItem value="amazon,google">Amazon + Google</SelectItem>
                   <SelectItem value="amazon">Amazon Rekognition</SelectItem>
                   <SelectItem value="google">Google Vision</SelectItem>
                   <SelectItem value="microsoft">Microsoft Azure</SelectItem>
                   <SelectItem value="clarifai">Clarifai</SelectItem>
                   <SelectItem value="api4ai">API4AI</SelectItem>
                   <SelectItem value="sentisight">Sentisight</SelectItem>
                 </SelectContent>
                </Select>
              </div>

              <div className="flex justify-center">
                <Button 
                  onClick={processImage} 
                  disabled={!uploadedFile || isProcessing} 
                  className="hover-lift px-8"
                  size="lg"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Detecting Objects...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Detect Objects
                    </>
                  )}
                </Button>
              </div>

              {error && (
                <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 dark:text-red-200">{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>

        {results && (
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle>Detection Results</CardTitle>
              <CardDescription>Objects detected in your image</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea 
                value={formatResults(results)} 
                readOnly 
                className="min-h-[300px] font-mono text-sm bg-muted/50 input-enhanced" 
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
